import {action, observable} from 'mobx';

export enum BreakpointMaxWidth {
	Xs = 767,
	Sm = 1024,
	Md = 1439,
}

export enum ResponsiveVisibility {
	Visible = 1,
	Hidden = 2,
}

export namespace ResponsiveVisibility {
	export function isVisible(responsiveVisibility: ResponsiveVisibility) {
		return responsiveVisibility === ResponsiveVisibility.Visible;
	}
}

export enum ResponsiveBreakpoint {
	Xs = 1 << 0,
	Sm = 1 << 1,
	Md = 1 << 2,
	Lg = 1 << 3
}

export namespace ResponsiveBreakpoint {
	export function isXs(responsiveBreakpoint: ResponsiveBreakpoint) {
		return responsiveBreakpoint & ResponsiveBreakpoint.Xs;
	}
	export function isSm(responsiveBreakpoint: ResponsiveBreakpoint) {
		return responsiveBreakpoint & ResponsiveBreakpoint.Sm;
	}
	export function isMd(responsiveBreakpoint: ResponsiveBreakpoint) {
		return responsiveBreakpoint & ResponsiveBreakpoint.Md;
	}
	export function isLg(responsiveBreakpoint: ResponsiveBreakpoint) {
		return responsiveBreakpoint & ResponsiveBreakpoint.Lg;
	}
}

type MediaMatcher = MediaQueryList | MediaQueryListEvent;

export class ResponsiveHelper {
	// Media queries should match bootstrap-variables.less
	readonly xsMediaQuery = `screen and (max-width: ${BreakpointMaxWidth.Xs}px)`;
	readonly smMediaQuery = `screen and (min-width: ${BreakpointMaxWidth.Xs + 1}px) and (max-width: ${BreakpointMaxWidth.Sm}px)`;
	readonly mdMediaQuery = `screen and (min-width: ${BreakpointMaxWidth.Sm + 1}px) and (max-width: ${BreakpointMaxWidth.Md}px)`;
	readonly lgMediaQuery = `screen and (min-width: ${BreakpointMaxWidth.Md + 1}px)`;

	@observable isXs: boolean = false;
	@observable isSm: boolean = false;
	@observable isMd: boolean = false;
	@observable isLg: boolean = false;

	constructor(contentWindow?: Window) {
		// We might not have a window if we are inside a unit test
		if (!contentWindow || !contentWindow.matchMedia) {
			return;
		}

		const xsMediaMatcher = contentWindow.matchMedia(this.xsMediaQuery);
		this.updateIsXs(xsMediaMatcher);
		xsMediaMatcher.addListener(this.updateIsXs);

		const smMediaMatcher = contentWindow.matchMedia(this.smMediaQuery);
		this.updateIsSm(smMediaMatcher);
		smMediaMatcher.addListener(this.updateIsSm);

		const mdMediaMatcher = contentWindow.matchMedia(this.mdMediaQuery);
		this.updateIsMd(mdMediaMatcher);
		mdMediaMatcher.addListener(this.updateIsMd);

		const lgMediaMatcher = contentWindow.matchMedia(this.lgMediaQuery);
		this.updateIsLg(lgMediaMatcher);
		lgMediaMatcher.addListener(this.updateIsLg);
	}

	isVisible = (visibility: ResponsiveVisibility, responsiveBreakpoints: ResponsiveBreakpoint) => {
		if(this.isXs && ResponsiveBreakpoint.isXs(responsiveBreakpoints)) {
			return ResponsiveVisibility.isVisible(visibility);
		}
		if(this.isSm && ResponsiveBreakpoint.isSm(responsiveBreakpoints)) {
			return ResponsiveVisibility.isVisible(visibility);
		}
		if(this.isMd && ResponsiveBreakpoint.isMd(responsiveBreakpoints)) {
			return ResponsiveVisibility.isVisible(visibility);
		}
		if(this.isLg && ResponsiveBreakpoint.isLg(responsiveBreakpoints)) {
			return ResponsiveVisibility.isVisible(visibility);
		}
		return !ResponsiveVisibility.isVisible(visibility);
	}

	@action
	private updateIsXs = ({ matches }: MediaMatcher) => {
		this.isXs = matches;
	}

	@action
	private updateIsSm = ({ matches }: MediaMatcher) => {
		this.isSm = matches;
	}

	@action
	private updateIsMd = ({ matches }: MediaMatcher) => {
		this.isMd = matches;
	}

	@action
	private updateIsLg = ({ matches }: MediaMatcher) => {
		this.isLg = matches;
	}
}

export const makeResponsiveHelper = (contentWindow: Window) => new ResponsiveHelper(contentWindow);

export const responsiveHelper = makeResponsiveHelper(window);
